/**
 * "Test selected" — one test for the people ticked in a Review folder (plan
 * 2026-10-15-005 addendum). Generalises {@link useMemberTest}'s one-person
 * start to a list: the SAME request (`sendMemberTest`, the full routed roster
 * plus `testMemberIds`), the SAME per-person pending / recorded-test / failure
 * keys in `member-test-store` (so each ticked person's row Test button greys
 * and shows the result like a single press), and the SAME persistent
 * "Test send not started" message a refused Send leaves (`review-cards-store`).
 *
 * No effect in here — a click handler only — so StrictMode has nothing to
 * double-fire. `playId` and the ids are the click-time values: the request may
 * settle after the rep moved on, and everything lands under THAT play.
 */
import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { addErrorToast, addToast } from '@clay-run/glaze/toast/toast-queue';

import { WriteRefusedError } from '../../../_shared/fetch-error';
import { useApi } from '../../../_shared/hooks';
import { listPlaySendsRequest } from '../../api-client';
import { playSendsQueryKey } from '../../dispatch-inspector-logic';
import { memberTestFailureOf, memberTestWorking } from '../../member-test-logic';
import {
  clearMemberFailure,
  clearTestPending,
  markTestPending,
  memberTestKey,
  recordMemberFailure,
  recordMemberTest,
  useMemberTestStore,
} from '../../member-test-store';
import { sendMemberTest } from '../../mutations';
import { openDispatchLog, openReview, setSendFailure } from '../../review-cards-store';
import { clearGroupSelection } from '../../review-selection-store';
import { sendFailureInline, sendFailureOf, sendResultToast } from '../../review-step-logic';
import { PLAYS_LIBRARY_QUERY_KEY } from '../../use-plays-library';
import { playsPrefixKey } from '../../query-keys';

export interface UseTestSelectedArgs {
  playId: string;
  /** The folder's selection key (`review-selection-store`). */
  groupKey: string;
  /** `tally.routedMemberIds`; `null` while the ladder is unavailable. */
  routedMemberIds: string[] | null;
  /** The group's reason a test cannot be sent, or `null`. */
  disabledReason: string | null;
  /** The people the next press would test (the folder's batch) — checked against each one's own "test still running" lock. */
  batchIds: readonly string[];
}

/** Why Test selected is greyed while a ticked person's own test is still going (the per-row Test is greyed for the same reason). */
export const TEST_SELECTED_WORKING_REASON =
  'A test for someone you ticked is still running. You can test them again when it finishes.';

export function useTestSelected({ playId, groupKey, routedMemberIds, disabledReason, batchIds }: UseTestSelectedArgs) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  // Any person of THIS press already has a request in flight (a double click, or a one-person Test just pressed).
  const anyPending = useMemberTestStore((state) =>
    Object.keys(state.pendingKeys).some((key) => key.startsWith(`${playId}:`)),
  );

  // The sends list the Dispatch log already polls (no interval of our own); only read once someone ticked has a recorded test.
  const recordedForBatch = useMemberTestStore((state) => state.testsByKey);
  const batchRecorded = batchIds.map((id) => recordedForBatch[memberTestKey(playId, id)]);
  const anyRecorded = batchRecorded.some((entry) => entry !== undefined);
  const sendsQuery = useQuery({
    queryKey: playSendsQueryKey(playId),
    queryFn: () => listPlaySendsRequest(apiFetch, playId),
    enabled: anyRecorded,
  });
  // Same rule as `useMemberTest`: a sends list that cannot be read must not leave the button greyed forever.
  const sendsReadable = !(sendsQuery.isError && sendsQuery.data === undefined);
  const anyWorking = sendsReadable && batchRecorded.some((entry) => memberTestWorking(entry, sendsQuery.data));

  // The play this hook is showing NOW (null once unmounted); reset at the top of the effect so StrictMode's setup→cleanup→setup leaves it correct.
  const livePlayId = useRef<string | null>(playId);
  useEffect(() => {
    livePlayId.current = playId;
    return () => {
      livePlayId.current = null;
    };
  }, [playId]);

  /** `ids` are the people to test, already capped and in folder order. */
  async function start(ids: readonly string[]): Promise<void> {
    if (routedMemberIds === null || disabledReason !== null || ids.length === 0) return;
    // Synchronous re-entry guard: the button only greys after a re-render, so a fast second click would otherwise send again.
    const { pendingKeys } = useMemberTestStore.getState();
    if (Object.keys(pendingKeys).some((key) => key.startsWith(`${playId}:`))) return;
    setSendFailure(null);
    for (const id of ids) {
      clearMemberFailure(playId, id);
      markTestPending(playId, id);
    }
    try {
      const response = await sendMemberTest(apiFetch, playId, routedMemberIds, ids);
      for (const id of ids) recordMemberTest(playId, id, response.dispatchId);
      clearGroupSelection(playId, groupKey);
      openDispatchLog();
      addToast(sendResultToast(response, true));
      void queryClient.invalidateQueries({ queryKey: playSendsQueryKey(playId) });
    } catch (error) {
      // core `apiFetch` already told the rep about a refused write — never twice.
      if (error instanceof WriteRefusedError) return;
      // The server may have accepted it: look at the true state before the rep reads a word.
      void queryClient.invalidateQueries({ queryKey: playSendsQueryKey(playId) });
      void queryClient.invalidateQueries({ queryKey: playsPrefixKey() });
      void queryClient.invalidateQueries({ queryKey: PLAYS_LIBRARY_QUERY_KEY });
      const failure = sendFailureOf(error);
      if (failure.status === 'info') addToast({ content: failure.content, status: 'info' });
      else addErrorToast({ content: failure.content });
      const words = memberTestFailureOf(error);
      if (words !== null) for (const id of ids) recordMemberFailure(playId, id, words);
      // The ticks stay (the rep can press again). The reason stays above the Send button.
      setSendFailure({ ...sendFailureInline(failure, 'test', new Date()), playId });
      // Kept under THIS play; a failure that lands after the rep moved on must not open the new play's card.
      if (livePlayId.current === playId) openReview();
    } finally {
      for (const id of ids) clearTestPending(playId, id);
    }
  }

  return {
    start,
    disabled: disabledReason !== null || routedMemberIds === null || anyPending || anyWorking,
    /** The plain sentence for the button's title: the group's reason, else the running-test one. */
    reason: disabledReason ?? (anyWorking ? TEST_SELECTED_WORKING_REASON : null),
  };
}
